import { useEffect, useState } from 'react';
import api from '../lib/api';
import { fmtCurrency } from '../lib/utils';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend,
  PieChart, Pie, Cell,
} from 'recharts';
import { DollarSign, TrendingUp, Award, Wallet } from 'lucide-react';
import { KpiCard } from '../components/dashboard/KpiCard';

const NAVY = '#0E1944';
const MAGENTA = '#E91E8C';
const COLORS = [NAVY, MAGENTA, '#10B981', '#F59E0B', '#3B82F6', '#8B5CF6'];

export default function SalesDashboardPage() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get('/analytics/sales').then((r) => setData(r.data)); }, []);
  if (!data) return <div className="text-slate-500 text-sm">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="sales-dashboard">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <KpiCard label="Total Revenue" value={fmtCurrency(data.total_revenue)} icon={DollarSign} testid="sales-total-revenue" />
        <KpiCard label="Partners Closed" value={data.total_won} icon={Award} accent="navy" testid="sales-total-won" />
        <KpiCard label="Average Deal" value={fmtCurrency(data.average_deal_size)} icon={TrendingUp} testid="sales-avg-deal" />
        <KpiCard label="Total Commission" value={fmtCurrency(data.total_commission)} icon={Wallet} accent="navy" testid="sales-commission" />
      </div>

      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <h3 className="font-heading text-lg font-bold text-navy mb-4">Monthly Revenue Trend (12 months)</h3>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data.monthly_revenue_trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
            <XAxis dataKey="month" stroke="#94A3B8" fontSize={11} />
            <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `$${v / 1000}k`} />
            <Tooltip formatter={(v) => fmtCurrency(v)} contentStyle={{ borderRadius: 12, border: '1px solid #E2E8F0' }} />
            <Line type="monotone" dataKey="revenue" stroke={MAGENTA} strokeWidth={3} dot={{ r: 5, fill: MAGENTA }} activeDot={{ r: 7 }} />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Revenue by Representative</h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.revenue_by_rep} layout="vertical">
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" horizontal={false} />
              <XAxis type="number" stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `$${v / 1000}k`} />
              <YAxis type="category" dataKey="name" stroke="#94A3B8" fontSize={11} width={110} />
              <Tooltip formatter={(v) => fmtCurrency(v)} />
              <Bar dataKey="revenue" fill={NAVY} radius={[0, 8, 8, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
          <h3 className="font-heading text-lg font-bold text-navy mb-4">Revenue by Package</h3>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={data.revenue_by_package} dataKey="revenue" nameKey="name" outerRadius={100} label={(e) => `$${e.revenue / 1000}k`}>
                {data.revenue_by_package.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Pie>
              <Tooltip formatter={(v) => fmtCurrency(v)} />
              <Legend wrapperStyle={{ fontSize: '10px' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
